import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronUp, ChevronRight, Keyboard, Mic, X, Check, Minus } from 'lucide-react';
import { useVoiceSession } from '../hooks/useVoiceSession';
import './VoiceMode.css';

const STATE_WORD = {
  connecting: 'Connecting…',
  listening: 'Listening',
  thinking: 'Thinking',
  speaking: 'Speaking',
  error: 'Connection lost',
};

const HINT = {
  listening: 'Tap the mic or close to leave voice mode',
  thinking: 'You can still interrupt',
  speaking: 'Start talking to interrupt',
};

/**
 * Full-screen voice conversation overlay. Rendered conditionally by
 * ChatContainer (not a route) so closing it returns to the exact same chat
 * state instantly. See frontend/design-voice-states/ for the visual
 * reference this was built from.
 */
const VoiceMode = ({ sessionId, onClose, onUserTranscript, onAssistantReply, onTurnCommitted, onViewResult }) => {
  const [lastUserText, setLastUserText] = useState('');
  const [lastAssistantText, setLastAssistantText] = useState('');

  const handleUserTranscript = useCallback((text) => {
    setLastUserText(text);
    if (onUserTranscript) onUserTranscript(text);
  }, [onUserTranscript]);

  const handleAssistantReply = useCallback((text) => {
    setLastAssistantText(text);
    if (onAssistantReply) onAssistantReply(text);
  }, [onAssistantReply]);

  const [cardIndex, setCardIndex] = useState(0);
  const touchStartX = useRef(null);

  const { voiceState, partialTranscript, assistantPartial, resultCards, startVoice, stopVoice } = useVoiceSession({
    sessionId,
    onUserTranscript: handleUserTranscript,
    onAssistantReply: handleAssistantReply,
    // The chat transcript is built from committed turns only — see
    // useVoiceSession's protocol note. These two callbacks stay wired to the
    // on-screen caption, which is allowed to show a turn that is still in
    // flight; the chat is not.
    onTurnCommitted,
  });

  useEffect(() => {
    startVoice();
    // Only on mount — startVoice/stopVoice identity can change with props,
    // but we want exactly one session per time VoiceMode is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    stopVoice();
    onClose();
  };

  const resultCard = resultCards[cardIndex] || null;
  const cardCount = resultCards.length;

  // A new set of results always starts from the front, otherwise the index
  // left over from the previous search points into the wrong list.
  useEffect(() => { setCardIndex(0); }, [resultCards]);

  const showCard = useCallback((next) => {
    setCardIndex((prev) => {
      if (cardCount === 0) return 0;
      return Math.min(Math.max(next, 0), cardCount - 1);
    });
  }, [cardCount]);

  // Swipe, because this is a phone-first surface and the caller is holding the
  // handset. The arrows stay for pointer devices and for accessibility.
  const handleTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;   // ignore taps and jitter
    showCard(cardIndex + (dx < 0 ? 1 : -1));
  };

  const handleViewDetails = () => {
    stopVoice();
    if (onViewResult && resultCard) onViewResult(resultCard);
    onClose();
  };

  let caption = '';
  if (voiceState === 'listening') caption = partialTranscript;
  else if (voiceState === 'thinking') caption = lastUserText;
  // While speaking, show the reply as it is being said. lastAssistantText
  // only lands once the whole turn is finished, so using it here meant the
  // words appeared after they had already been spoken.
  else if (voiceState === 'speaking') caption = assistantPartial || lastAssistantText;

  const displayState = ['listening', 'thinking', 'speaking', 'error'].includes(voiceState)
    ? voiceState
    : 'connecting';

  return (
    <div className="voice-mode" role="dialog" aria-modal="true" aria-label="Voice conversation">
      <div className="voice-top">
        <button type="button" className="voice-back-link" onClick={handleClose}>
          <ChevronLeft size={14} strokeWidth={2.5} />
          Chat
        </button>
      </div>

      <div className="voice-body">
        <div className={`voice-orb-stage voice-orb-${displayState}`}>
          <div className="voice-orb-ring voice-orb-ring-1" />
          <div className="voice-orb-ring voice-orb-ring-2" />
          <div className="voice-orb-core" />
        </div>

        <div>
          <div className="voice-state-word">{STATE_WORD[displayState]}</div>
          <div className="voice-caption">{caption}</div>
        </div>

        {voiceState === 'error' && (
          <p className="voice-error-hint">
            Lost the voice connection. Close and tap the mic to try again.
          </p>
        )}

        {/* Shown in every state once a recommendation exists. Restricting
            it to speaking/listening hid it during 'thinking', so a card
            flickered away the moment the user started their next question —
            long before they had time to read it. */}
        {resultCard && (
          <div
            className="voice-result-card"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="voice-card-head">
              <div
                className="voice-card-thumb"
                style={resultCard.thumbnail_url ? { backgroundImage: `url(${resultCard.thumbnail_url})` } : undefined}
              />
              <div className="voice-card-info">
                <div className="voice-card-name">{resultCard.project_name}</div>
                {resultCard.location && <div className="voice-card-meta">{resultCard.location}</div>}
              </div>
            </div>

            {(resultCard.pros?.length > 0 || resultCard.watch_for?.length > 0) && (
              <div className="voice-card-scroll">
                <div className="voice-card-divider" />
                <div className="voice-pc-grid">
                  {resultCard.pros?.length > 0 && (
                    <div className="voice-pc-col">
                      <div className="voice-pc-label pros">Pros</div>
                      {resultCard.pros.map((p, i) => (
                        <div className="voice-pc-item pros" key={i}>
                          <Check size={12} strokeWidth={2.5} />
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {resultCard.watch_for?.length > 0 && (
                    <div className="voice-pc-col">
                      <div className="voice-pc-label watch">Watch for</div>
                      {resultCard.watch_for.map((p, i) => (
                        <div className="voice-pc-item watch" key={i}>
                          <Minus size={12} strokeWidth={2.5} />
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="voice-card-foot">
              <span className="voice-card-count">
                {/* Position in the set the caller can actually swipe through.
                    This line used to read "1 of 5 matches" to a buyer with
                    nothing in range: total_matches counts compounds holding a
                    unit that fits them, and the search matches on area first,
                    so five area hits can contain zero affordable units. */}
                {resultCard.has_matching_units === false
                  ? (resultCard.availability_note || 'No matching units')
                  : cardCount > 1
                    ? `${cardIndex + 1} of ${cardCount}`
                    : '1 match'}
              </span>
              {/* Opening the compound page tears down the Live session, and
                  this used to be an 11.5px link with no padding — a mis-tap
                  during a call ended it with no warning and no way back. The
                  consequence is now stated, and the target is a real one. The
                  call itself is no longer lost work: the transcript and the
                  results are persisted, so the conversation is waiting in the
                  chat behind the details page. */}
              <button
                type="button"
                className="voice-card-link"
                onClick={handleViewDetails}
                aria-label="View compound details. This ends the voice call."
              >
                <span className="voice-card-link-main">
                  View details
                  <ChevronRight size={11} strokeWidth={2.5} />
                </span>
                <span className="voice-card-link-note">ends the call</span>
              </button>
            </div>

            {cardCount > 1 && (
              <div className="voice-card-nav">
                <button
                  type="button"
                  className="voice-card-arrow"
                  onClick={() => showCard(cardIndex - 1)}
                  disabled={cardIndex === 0}
                  aria-label="Previous property"
                >
                  <ChevronLeft size={16} strokeWidth={2.5} />
                </button>
                <div className="voice-card-dots">
                  {resultCards.map((c, i) => (
                    <button
                      type="button"
                      key={c.compound_id ?? i}
                      className={`voice-card-dot${i === cardIndex ? ' active' : ''}`}
                      onClick={() => showCard(i)}
                      aria-label={`Property ${i + 1} of ${cardCount}`}
                      aria-current={i === cardIndex}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="voice-card-arrow"
                  onClick={() => showCard(cardIndex + 1)}
                  disabled={cardIndex === cardCount - 1}
                  aria-label="Next property"
                >
                  <ChevronRight size={16} strokeWidth={2.5} />
                </button>
              </div>
            )}
          </div>
        )}

        {!resultCard && voiceState === 'listening' && !caption && (
          <div className="voice-hint" style={{ padding: 0 }}>
            <ChevronUp size={12} strokeWidth={2.5} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Go ahead, I'm listening
          </div>
        )}
      </div>

      <div className="voice-hint">{HINT[voiceState] || ''}</div>

      <div className="voice-bottom">
        <button type="button" className="voice-btn" onClick={handleClose} aria-label="Switch to text">
          <Keyboard size={18} />
        </button>
        <div className={`voice-btn voice-btn-mic ${displayState}`} aria-hidden>
          <Mic size={22} />
        </div>
        <button type="button" className="voice-btn voice-btn-close" onClick={handleClose} aria-label="Close voice mode">
          <X size={18} strokeWidth={2.3} />
        </button>
      </div>
    </div>
  );
};

export default VoiceMode;
